/**
 * 이 여행을 코스로 남기기 — 여행이 끝난 뒤 V6 하단에 나타납니다.
 *
 * 자리가 종료 화면(MeetingFinishedView)이 아닌 이유
 *   여행 모임은 meetingType === 'TRAVEL' 로 V6 에 고정 분기되어 그 화면을
 *   타지 않습니다. V6 는 예정·진행·종료를 한 화면에서 Day 상태로만 구분합니다.
 *
 * 새로 입력받는 건 제목과 한 줄뿐입니다
 *   일정은 이미 만들어져 있습니다. 여기서 또 장소를 고르라고 하면 아무도
 *   안 합니다. 요약 카드로 "이미 다 만들어져 있음" 을 먼저 보여 줍니다.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Section, SectionHeader, TextField, TextArea } from 'components/ds';
import { useAlert } from 'contexts/AlertContext';
import { TravelCourseRepository } from 'repository/travelCourseRepository';
import { unwrapData } from 'common/utils/apiResponse';
import styles from './CourseRegisterSection.module.css';

interface Props {
    meetingId: string;
    /** 요약 카드에 쓰는 값 — 화면이 이미 들고 있는 것을 넘겨받습니다 */
    regionName?: string | null;
    days?: number;
    placeNames?: string[];
    memberCount?: number;
    thumbnailUrl?: string | null;
    /** 등록 뒤 — 섹션을 감추는 등 */
    onRegistered?: (courseId: string) => void;
}

const TITLE_MAX = 40;
const SUMMARY_MAX = 100;

export const CourseRegisterSection: React.FC<Props> = ({
    meetingId, regionName, days, placeNames, memberCount, thumbnailUrl, onRegistered,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = useCallback(async () => {
        if (!title.trim() || busy) return;
        setBusy(true);
        try {
            const res = await TravelCourseRepository.create({
                meetingId,
                title: title.trim(),
                summary: summary.trim() || undefined,
            });
            const course = unwrapData<{ courseId?: string }>(res);
            if (!course?.courseId) {
                showAlert({ message: t('course.registerFailed') });
                return;
            }
            showAlert({ message: t('course.registered') });
            onRegistered?.(course.courseId);
            /* 방금 남긴 것을 바로 보여 줍니다 — 등록하고 아무 일도 안 일어나면
               제대로 된 건지 알 수 없습니다. */
            navigate(`/travel/course/${course.courseId}`);
        } catch {
            showAlert({ message: t('course.registerFailed') });
        } finally {
            setBusy(false);
        }
    }, [title, summary, busy, meetingId, showAlert, t, onRegistered, navigate]);

    /* 장소는 셋까지만 늘어놓고 나머지는 수로 줄입니다 */
    const names = placeNames || [];
    const shown = names.slice(0, 3).join(' · ');
    const rest = names.length - Math.min(3, names.length);

    return (
        <Section divided>
            <SectionHeader title={t('course.registerTitle')} sub={t('course.registerSub')} />

            {/* 요약 — "이미 다 만들어져 있음" 을 먼저 보여 줍니다 */}
            <div className={styles.summaryCard}>
                {thumbnailUrl && (
                    <span className={styles.thumb}>
                        <img src={thumbnailUrl} alt="" loading="lazy" />
                    </span>
                )}
                <span className={styles.summaryBody}>
                    <b className={styles.summaryTitle}>
                        {regionName || ''}{' '}
                        {days && days > 1
                            ? t('course.nights', { n: days - 1, d: days })
                            : t('course.oneDay')}
                    </b>
                    {names.length > 0 && (
                        <span className={styles.places}>
                            {shown}{rest > 0 ? t('course.andMore', { n: rest }) : ''}
                        </span>
                    )}
                    {memberCount != null && memberCount > 0 && (
                        <span className={styles.members}>
                            {t('course.withMembers', { n: memberCount })}
                        </span>
                    )}
                </span>
            </div>

            <div className={styles.form}>
                <TextField
                    label={t('course.titleLabel')}
                    required
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    maxLength={TITLE_MAX}
                    showCount
                    placeholder={t('course.titlePlaceholder')}
                />
                <TextArea
                    label={t('course.summaryLabel')}
                    optionalText={t('course.summaryOptional')}
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    rows={3}
                    maxLength={SUMMARY_MAX}
                    showCount
                    placeholder={t('course.summaryPlaceholder')}
                />
            </div>

            {/* 스냅샷임을 미리 알립니다 — 나중에 "왜 안 바뀌지" 가 되지 않게 */}
            <p className={styles.note}>{t('course.snapshotNote')}</p>

            <Button
                variant="primary"
                size="lg"
                block
                disabled={!title.trim() || busy}
                onClick={submit}
            >
                {busy ? t('course.registering') : t('course.register')}
            </Button>
        </Section>
    );
};
